import { useState } from 'react'

// ── Sign-in shell (redesign, Phase 6) ────────────────────────────────────────
// One layout for every door into the portal: client sign-in, contractor
// sign-in, contractor setup and password reset. A quiet brand panel on the
// left and the form on the right; on a phone the panel shrinks to a header
// above the form. Both sides read the theme tokens, so neither is ever a dark
// block with reversed text, and dark mode still works.

export function AuthLayout({ children, eyebrow, headline = 'Your projects, reviews and invoices in one place.', lede = "Clients, team and contractors all sign in here. You'll see what's yours." }) {
  return (
    <div className="auth-page" style={{ display: 'flex', flexWrap: 'wrap', minHeight: '100vh', background: 'var(--card)', fontFamily: 'var(--fb)' }}>
      <section className="auth-brand" aria-label="Abstract Creative" style={{
        flex: '1 1 520px', minWidth: 0, boxSizing: 'border-box', background: 'var(--off)', color: 'var(--txt)', borderRight: '1px solid var(--bdr)',
        padding: '48px 56px', display: 'flex', flexDirection: 'column', gap: 24,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
<img src="/logo-lockup.svg" alt="Abstract Creative" className="brand-logo-light" style={{ display: 'block', height: 48, width: 'auto' }} />
          <img src="/logo-lockup-light.svg" alt="Abstract Creative" className="brand-logo-dark" style={{ height: 48, width: 'auto' }} />
          {eyebrow && <span style={{ fontSize: 13, color: 'var(--mut)' }}>· {eyebrow}</span>}
        </div>
        <div className="auth-brand-spacer" style={{ flex: 1 }} />
        <h1 className="auth-brand-headline" style={{ margin: 0, maxWidth: 520, fontSize: 44, lineHeight: 1.1, fontWeight: 800, letterSpacing: '-0.02em' }}>
          {headline}
        </h1>
        <p className="auth-brand-extra" style={{ margin: 0, maxWidth: 460, fontSize: 17, lineHeight: 1.55, color: 'var(--mut)' }}>{lede}</p>
        <div className="auth-brand-extra" style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
          {['Review and approve work', 'Sign proposals', 'Pay invoices'].map(t => (
            <span key={t} style={{ display: 'flex', alignItems: 'center', height: 36, padding: '0 14px', borderRadius: 18, background: 'var(--card)', border: '1px solid var(--bdr)', fontSize: 13 }}>{t}</span>
          ))}
        </div>
        <div className="auth-brand-spacer" style={{ flex: 1 }} />
        <div className="auth-brand-extra" style={{ fontSize: 13, color: 'var(--mut)' }}>
          Need help? <a href="mailto:efren@abstract-creative.com" style={{ color: 'var(--txt)', fontWeight: 700 }}>efren@abstract-creative.com</a>
        </div>
      </section>

      <main className="auth-main" style={{ flex: '1 1 480px', minWidth: 0, boxSizing: 'border-box', padding: '48px 24px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ width: '100%', maxWidth: 400, display: 'flex', flexDirection: 'column', gap: 16 }}>
          {children}
        </div>
      </main>
    </div>
  )
}

export function AuthHeading({ title, sub, id }) {
  return (
    <div>
      <h2 id={id} style={{ margin: 0, fontSize: 28, fontWeight: 800, color: 'var(--navy)', letterSpacing: '-0.01em', lineHeight: 1.15 }}>{title}</h2>
      {sub && <p style={{ margin: '6px 0 0', fontSize: 15, lineHeight: 1.55, color: 'var(--mut)' }}>{sub}</p>}
    </div>
  )
}

const inputBase = {
  width: '100%', height: 48, boxSizing: 'border-box', padding: '0 14px',
  border: '1px solid var(--bdr2)', borderRadius: 10, fontFamily: 'inherit',
  fontSize: 16, fontWeight: 400, color: 'var(--txt)', background: 'var(--card)', outline: 'none',
}

const labelBase = { display: 'flex', flexDirection: 'column', gap: 6, fontSize: 13, fontWeight: 700, color: 'var(--txt)' }

export function AuthField({ label, aside, invalid, inputRef, ...input }) {
  return (
    <label style={labelBase}>
      {aside ? <span style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}><span>{label}</span>{aside}</span> : label}
      <input ref={inputRef} aria-invalid={invalid || undefined} className="auth-input" style={{ ...inputBase, borderColor: invalid ? 'var(--danger)' : undefined }} {...input} />
    </label>
  )
}

// A password input with a Show/Hide toggle. The toggle is a real button with
// aria-pressed so screen readers hear the state.
export function PasswordField({ label = 'Password', aside, invalid, ...input }) {
  const [show, setShow] = useState(false)
  return (
    <label style={labelBase}>
      {aside ? <span style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}><span>{label}</span>{aside}</span> : label}
      <span style={{ position: 'relative', display: 'flex' }}>
        <input type={show ? 'text' : 'password'} aria-invalid={invalid || undefined} className="auth-input"
          style={{ ...inputBase, paddingRight: 72, borderColor: invalid ? 'var(--danger)' : undefined }} {...input} />
        <button type="button" onClick={() => setShow(s => !s)} aria-pressed={show}
          aria-label={show ? 'Hide password' : 'Show password'}
          style={{ position: 'absolute', right: 6, top: 6, height: 36, padding: '0 10px', border: 'none', borderRadius: 7, background: 'transparent', fontFamily: 'inherit', fontSize: 13, fontWeight: 700, color: 'var(--navy)', cursor: 'pointer' }}>
          {show ? 'Hide' : 'Show'}
        </button>
      </span>
    </label>
  )
}

export function AuthError({ children }) {
  if (!children) return null
  return (
    <p role="alert" style={{ margin: 0, padding: '10px 12px', borderRadius: 8, background: 'var(--danger-bg)', fontSize: 13, lineHeight: 1.5, color: 'var(--danger)' }}>
      {children}
    </p>
  )
}

export function AuthNotice({ children, tone = 'warning' }) {
  return (
    <p role="status" style={{ margin: 0, padding: '10px 12px', borderRadius: 8, background: `var(--${tone}-bg)`, fontSize: 13, lineHeight: 1.5, color: `var(--${tone})` }}>
      {children}
    </p>
  )
}

function Spinner() {
  return <span aria-hidden="true" style={{ width: 14, height: 14, border: '2px solid var(--bdr2)', borderTopColor: 'var(--accent)', borderRadius: '50%', animation: 'spin 0.7s linear infinite' }} />
}

export function AuthPrimary({ children, loading, loadingLabel, disabled, ...btn }) {
  const off = disabled || loading
  return (
    <button type="submit" disabled={off} {...btn} style={{
      height: 50, border: '1px solid color-mix(in srgb, var(--accent) 40%, transparent)', borderRadius: 10, background: 'var(--accent-soft)', color: 'var(--accent)',
      fontFamily: 'inherit', fontSize: 16, fontWeight: 800, cursor: off ? 'not-allowed' : 'pointer',
      opacity: off ? 0.6 : 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
      textDecoration: 'none', ...btn.style,
    }}>
      {loading ? <><Spinner />{loadingLabel || children}</> : children}
    </button>
  )
}

export function AuthSecondary({ children, icon, ...btn }) {
  return (
    <button type="button" {...btn} style={{
      height: 44, border: 'none', borderRadius: 10, background: 'var(--surf)', color: 'var(--navy)',
      fontFamily: 'inherit', fontSize: 14, fontWeight: 700, cursor: 'pointer',
      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, ...btn.style,
    }}>
      {icon}{children}
    </button>
  )
}

export function AuthLink({ children, ...btn }) {
  return (
    <button type="button" {...btn} style={{ alignSelf: 'flex-start', padding: 0, border: 'none', background: 'none', fontFamily: 'inherit', fontSize: 14, fontWeight: 700, color: 'var(--navy)', cursor: 'pointer', textDecoration: 'underline', textUnderlineOffset: 3, ...btn.style }}>
      {children}
    </button>
  )
}

export function BackLink({ onClick, href, children = 'Back to sign in' }) {
  const style = { alignSelf: 'flex-start', padding: 0, border: 'none', background: 'none', fontFamily: 'inherit', fontSize: 13, fontWeight: 700, color: 'var(--navy)', cursor: 'pointer' }
  if (href) return <a href={href} style={style}>‹ {children}</a>
  return <button type="button" onClick={onClick} style={style}>‹ {children}</button>
}

export function StatusIcon({ kind = 'mail' }) {
  const ok = kind === 'check'
  return (
    <span aria-hidden="true" style={{ width: 48, height: 48, borderRadius: 12, background: ok ? 'var(--success-bg)' : 'var(--info-bg)', color: ok ? 'var(--success)' : 'var(--info)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      {ok
        ? <svg width="22" height="22" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M3 8.5l3 3 7-7" strokeLinecap="round" strokeLinejoin="round" /></svg>
        : <svg width="22" height="22" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5"><rect x="2" y="3.5" width="12" height="9" rx="1.5" /><path d="M2.5 4.5L8 9l5.5-4.5" strokeLinecap="round" strokeLinejoin="round" /></svg>}
    </span>
  )
}

export const MailIcon = (
  <svg aria-hidden="true" width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5"><rect x="2" y="3.5" width="12" height="9" rx="1.5" /><path d="M2.5 4.5L8 9l5.5-4.5" strokeLinecap="round" strokeLinejoin="round" /></svg>
)

export function AuthFooter({ children }) {
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', gap: 8, paddingTop: 12, borderTop: '1px solid var(--bdr)', fontSize: 14, color: 'var(--mut)' }}>
      {children}
    </div>
  )
}

export function PasswordStrengthBar({ score = 0 }) {
  const labels = ['', 'weak', 'fair', 'good', 'strong']
  const colour = score >= 3 ? '#15803D' : score === 2 ? '#B45309' : '#B42318'
  return (
    <div role="img" aria-label={score ? `Password strength: ${labels[score]}` : 'Password strength: not set'} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
      {[1, 2, 3, 4].map(i => (
        <span key={i} style={{ flex: 1, height: 5, borderRadius: 3, background: score >= i ? colour : 'var(--surf)' }} />
      ))}
      <span style={{ minWidth: 48, textAlign: 'right', fontSize: 12, fontWeight: 600, color: 'var(--mut)' }}>{labels[score]}</span>
    </div>
  )
}

export const footerLinkStyle ={ fontWeight: 700, color: 'var(--navy)', textDecoration: 'underline', textUnderlineOffset: 3, background: 'none', border: 'none', padding: 0, fontFamily: 'inherit', fontSize: 'inherit', cursor: 'pointer' }
